import { createSlice } from "@reduxjs/toolkit";

const defaultProduct = {
  id: 1,

  // basic
  name: "",
  weight: "",
  styleCode: "",
  locked: false,

  // 👕 product identity (NEW)
  brandName: "",
  gender: "",
  garmentType: "",
  fitType: "",

  // attributes
  color: "",
  comboOf: "",
  fabric: "",
  fitShape: "",
  genericName: "",
  length: "",
  neck: "",
  occasion: "",
  pattern: "",
  printOrPatternType: "",
  sleeveLength: "",
  sleeveStyling: "",
  stitchType: "",

  // 🌍 manufacturing
  countryOfOrigin: "",
  manufacturerName: "",
  manufacturerAddress: "",
  manufacturerPincode: "",

  // 👤 model info (NEW)
  modelHeight: "",
  modelWearingSize: "",

  // 👗 outfit/set info (NEW)
  isSet: false,
  setItemsCount: 0,
  includedItems: [],

  // media
  images: [],
  selectedSizes: [],

  // inventory
  sizes: [],

  // lifecycle (NEW)
  status: "draft",
  isActive: true,
  returnAllowed: true,
  returnDays: 7,
  refundAllowed: true,
  exchangeAllowed: true,
};

const initialState = {
  products: [defaultProduct],
  activeIndex: 0,
  copyAll: true,
};

const productSlice = createSlice({
  name: "product",
  initialState,

  reducers: {
    setProducts(state, action) {
      state.products = action.payload;
    },

    setActiveIndex(state, action) {
      state.activeIndex = action.payload;
    },

    addProduct(state, action) {
      state.products.push(action.payload);
      state.activeIndex = state.products.length - 1;
    },

    updateProductField(state, action) {
      const { index, key, value } = action.payload;

      if (!state.products[index]) return;

      state.products[index][key] = value;
    },

    setCopyAll(state, action) {
      state.copyAll = action.payload;
    },

    copyProduct(state, action) {
      const index = action.payload;
      const src = state.products[index];

      if (!src) return;

      const copy = structuredClone?.(src) || JSON.parse(JSON.stringify(src));
      copy.id = crypto.randomUUID();
      copy._id = undefined;

      copy.images = [
        { type: "front", url: "" },
        { type: "side", url: "" },
        { type: "back", url: "" },
        { type: "zoom", url: "" },
        { type: "extra", url: "" },
      ];

      copy.extraImages = [];

      state.products.push(copy);
      state.activeIndex = state.products.length - 1;
    },
    setExtraImages(state, action) {
      const { index, images } = action.payload;

      const product = state.products[index];
      if (!product) return;

      product.extraImages = images;
    },
    applySizes(state, action) {
      const { index, sizes } = action.payload;

      const product = state.products[index];
      if (!product) return;

      product.selectedSizes = sizes;

      if (!Array.isArray(product.sizes)) {
        product.sizes = [];
      }

      sizes.forEach((size) => {
        const exists = product.sizes.find((s) => s.size === size);

        if (!exists) {
          product.sizes.push({
            size,
            price: "",
            mrp: "",
            inventory: "",
            sku: "",
            bust: "",
            shoulder: "",
            waist: "",
            length: "",
            hip: "",
          });
        }
      });
    },
    removeSize(state, action) {
      const { index, size } = action.payload;

      const product = state.products[index];
      if (!product) return;

      product.selectedSizes = product.selectedSizes.filter((s) => s !== size);

      product.sizes = product.sizes.filter((s) => s.size !== size);
    },
    updateSizeField(state, action) {
      const { index, size, field, value } = action.payload;

      const product = state.products[index];
      if (!product) return;

      if (!Array.isArray(product.sizes)) {
        product.sizes = [];
      }

      let sizeObj = product.sizes.find((s) => s.size === size);

      if (!sizeObj) {
        sizeObj = {
          size,
          price: "",
          mrp: "",
          inventory: "",
          sku: "",
          bust: "",
          shoulder: "",
          waist: "",
          length: "",
          hip: "",
        };

        product.sizes.push(sizeObj);
      }

      sizeObj[field] = value;

      // COPY ALL
      if (state.copyAll) {
        product.selectedSizes.forEach((s) => {
          if (s === size) return;

          let target = product.sizes.find((x) => x.size === s);

          if (!target) {
            target = {
              size: s,
              price: "",
              mrp: "",
              inventory: "",
              sku: "",
              bust: "",
              shoulder: "",
              waist: "",
              length: "",
              hip: "",
            };
            product.sizes.push(target);
          }

          if (target[field] === "" || target[field] == null) {
            target[field] = value;
          }
        });
      }
    },
    deleteProduct(state, action) {
      const index = action.payload;

      state.products.splice(index, 1);

      state.activeIndex = Math.max(
        0,
        Math.min(state.activeIndex, state.products.length - 1),
      );
    },

    updateImage(state, action) {
      const { index, type, url } = action.payload;

      const product = state.products[index];
      if (!product) return;

      if (!Array.isArray(product.images)) {
        product.images = [];
      }

      const existing = product.images.find((img) => img.type === type);

      if (existing) {
        existing.url = url;
      } else {
        product.images.push({ type, url });
      }
    },

    removeImage(state, action) {
      const { index, type } = action.payload;

      const product = state.products[index];
      if (!product) return;

      product.images = product.images.filter((img) => img.type !== type);
    },
  },
});

export const {
  setProducts,
  setActiveIndex,
  updateProductField,
  applySizes,
  removeSize,
  copyProduct,
  addProduct,
  setCopyAll,
  updateSizeField,
  deleteProduct,
  updateImage,
  removeImage,
} = productSlice.actions;

export default productSlice.reducer;
